import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Box } from "@mantine/core";

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { TextWrapper } from "@/components/TextWrapper";
import { longUSDateTimeFormat } from "@/utils/datetime";

interface LabOrderResultsTrendGraphProps {
  results: Observation[];
  display: string;
}

interface GraphDataPoint {
  name: string;
  value: number;
  unit: string;
  timestamp: number;
}

interface TickProps {
  graphData: GraphDataPoint[];
  x: number;
  y: number;
  index: number;
  payload: { value: string };
}

export default function LabOrderResultsTrendGraph({
  display,
  results,
}: LabOrderResultsTrendGraphProps) {
  const graphData = useMemo(
    () =>
      results
        .map((result) => {
          const dateString = result.effectiveDateTime ?? result.meta?.lastUpdated ?? "";
          const { value, unit } = result.valueQuantity ?? { value: undefined, unit: "" };
          return {
            name: longUSDateTimeFormat(dateString),
            value,
            unit,
            timestamp: new Date(dateString).getTime(),
          };
        })
        .filter((result) => result.value !== undefined)
        .sort((a, b) => a.timestamp - b.timestamp) as GraphDataPoint[],
    [results],
  );

  const unit = useMemo(() => graphData[0]?.unit ?? "", [graphData]);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart
        data={graphData}
        margin={{
          top: 30,
          right: 0,
          left: 0,
          bottom: 20,
        }}
      >
        <CartesianGrid vertical={false} strokeDasharray="4" />
        <XAxis
          interval={0}
          dataKey="name"
          tick={(props) => <Tick {...props} graphData={graphData} />}
        />
        <YAxis
          tickMargin={5}
          domain={["auto", "auto"]}
          tick={{ fontSize: "var(--mantine-font-size-sm)" }}
          label={
            unit
              ? {
                  value: unit,
                  position: "top",
                  offset: 20,
                  fontWeight: "bold",
                  fontSize: "var(--mantine-font-size-md)",
                  fill: "var(--mantine-color-black)",
                }
              : undefined
          }
        />
        <Legend
          align="right"
          iconType="square"
          verticalAlign="top"
          wrapperStyle={{ top: 0, right: 0 }}
        />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const data = payload[0].payload as GraphDataPoint;
            return (
              <Box
                p="xs"
                bg="white"
                style={{ border: "1px solid var(--mantine-color-gray-4)", borderRadius: 4 }}
              >
                <TextWrapper size="xs" c="dimmed">
                  {data.name}
                </TextWrapper>
                <TextWrapper fw={600} size="sm">
                  {data.value} {data.unit}
                </TextWrapper>
              </Box>
            );
          }}
        />
        <Line
          type="linear"
          name={display}
          dataKey="value"
          strokeWidth={2}
          stroke="var(--mantine-color-blue-4)"
          dot={{ r: 4, fill: "var(--mantine-color-blue-4)" }}
          activeDot={{ r: 6, fill: "var(--mantine-color-blue-6)" }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

const Tick = ({ graphData, x, y, payload, index }: TickProps) => {
  const isFirst = index === 0;
  const isLast = index === graphData.length - 1;

  // Only show first and last ticks if more than 5 data points
  if (graphData.length > 5 && !isFirst && !isLast) {
    return <text />;
  }

  // Adjust text anchor: start for first, end for last, middle for others
  const textAnchor = isFirst ? "start" : isLast ? "end" : "middle";

  return (
    <text
      x={x}
      y={y + 15}
      textAnchor={textAnchor}
      style={{ fontSize: "var(--mantine-font-size-sm)" }}
    >
      {payload.value}
    </text>
  );
};
